import { INoteTranscriptSegment } from '@dataTypes/note.type'

export type NotesPanelTranscriptSegment = {
  timestamp: string
  speaker: string
  text: string
  highlighted?: boolean
}

const formatPanelTimestamp = (seconds: number): string => {
  const total = Math.max(0, Math.floor(seconds))
  const mins = Math.floor(total / 60)
  const secs = total % 60
  return `[${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}]`
}

export const mapTranscriptSegmentsToPanel = (
  segments: INoteTranscriptSegment[],
): NotesPanelTranscriptSegment[] =>
  segments.map((segment) => ({
    timestamp: formatPanelTimestamp(segment.start),
    speaker: segment.speaker,
    text: segment.text,
    highlighted: segment.role === 'doctor',
  }))

/** End time of the last segment, or 0 when empty. */
export const lastTranscriptEnd = (
  segments: INoteTranscriptSegment[],
): number => {
  if (segments.length === 0) return 0
  const last = segments[segments.length - 1]
  return Math.max(0, last.end)
}

/** Shift segment start/end by offsetSeconds (for appending a new take). */
export const offsetTranscriptSegments = (
  segments: INoteTranscriptSegment[],
  offsetSeconds: number,
): INoteTranscriptSegment[] => {
  const offset = Math.max(0, offsetSeconds)
  if (offset === 0) return segments.map((segment) => ({ ...segment }))
  return segments.map((segment) => ({
    ...segment,
    start: segment.start + offset,
    end: segment.end + offset,
  }))
}

/** Plain-text note body for AI context (keeps list/line structure lightly). */
export const stripNoteHtmlToPlainText = (
  html: string | null | undefined,
): string => {
  if (!html?.trim()) return ''
  return html
    .replace(/\r\n/g, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|h[1-6]|tr|li)>/gi, '\n')
    .replace(/<li[^>]*>/gi, '- ')
    .replace(/<(strong|b)[^>]*>/gi, '')
    .replace(/<\/(strong|b)>/gi, '')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

export const formatTranscriptLines = (
  segments: INoteTranscriptSegment[],
): string => {
  const lines = segments
    .map((segment) => {
      const text = segment.text.trim()
      if (!text) return null
      const speaker = segment.speaker.trim() || 'Speaker'
      return `${formatPanelTimestamp(segment.start)} ${speaker}: ${text}`
    })
    .filter((line): line is string => Boolean(line))
  return lines.join('\n')
}

/**
 * Build AI source context from the current note draft and/or transcript.
 * Used by transcript summary, quick-note Ask, and Structure/Tone regenerates.
 */
export const buildNotesAiUserSourceInput = (opts: {
  existingNoteHtml?: string | null
  transcriptSegments?: INoteTranscriptSegment[]
  userRequest?: string
}): string => {
  const existingNote = stripNoteHtmlToPlainText(opts.existingNoteHtml)
  const transcript = formatTranscriptLines(opts.transcriptSegments || [])
  const userRequest = opts.userRequest?.trim() || ''

  if (!existingNote && !transcript && !userRequest) return ''

  const parts: string[] = [
    'This is a dental-related request.',
    'Use the sources below to draft or update a dental clinical note.',
    'When sources conflict, prefer the transcript. Preserve accurate draft details not contradicted by the transcript.',
  ]

  if (existingNote) {
    parts.push('', 'Existing note draft:', existingNote)
  }
  if (transcript) {
    parts.push('', 'Transcript:', transcript)
  }
  if (userRequest) {
    parts.push('', 'User request:', userRequest)
  }

  return parts.join('\n')
}

export const buildTranscriptSummaryInput = (
  segments: INoteTranscriptSegment[],
  opts?: { existingNoteHtml?: string | null },
): string => {
  const existingNote = stripNoteHtmlToPlainText(opts?.existingNoteHtml)
  const transcript = formatTranscriptLines(segments)

  if (!transcript && !existingNote) return ''

  const parts: string[] = [
    'This is a dental-related request.',
    'Summarize into a dental clinical note summary using the sources below.',
    'Preserve dental findings, patient concerns, treatment details, follow-up items, and unanswered questions.',
    'When sources conflict, prefer the transcript. Preserve accurate draft details not contradicted by the transcript.',
  ]

  if (existingNote) {
    parts.push('', 'Existing note draft:', existingNote)
  }
  if (transcript) {
    parts.push('', 'Transcript:', transcript)
  } else {
    parts.push(
      '',
      'No transcript is available. Update the note draft using the selected structure/tone only.',
    )
  }

  return parts.join('\n')
}
